import { RecipeVisibilityType } from '@/enums/recipe-visibility-type';
import { ToolVisibilityType } from '@/enums/tool-visibility-type';
import { isModelNode } from '@/components/Nodes/utils/nodeUtils';
import { NodeType } from '@/enums/node-type.enum';
import type { NodeDefinition } from '@/types/api/nodeDefinition';
import type { Node } from '@/types/node';

export type WorkflowCommunityPublishBlocker = 'not_public' | 'custom_fonts' | 'figma_node' | 'unverified_models' | null;

export interface WorkflowCommunityPublishUnverifiedModel {
  displayName: string;
  key: string;
}

export interface WorkflowCommunityPublishCustomFontReference {
  fontFamilyKey: string;
  nodeId?: string;
  nodeName?: string;
}

export interface WorkflowCommunityPublishReadiness {
  blocker: WorkflowCommunityPublishBlocker;
  customFontFamilyKeys: string[];
  customFontReferences: WorkflowCommunityPublishCustomFontReference[];
  figmaNodeIds: string[];
  isPublic: boolean;
  isReady: boolean;
  unverifiedModels: WorkflowCommunityPublishUnverifiedModel[];
}

function getModelDisplayName(node: Node, matchingNodeTypes: NodeDefinition[]): string {
  const matchingNodeType = matchingNodeTypes[0];
  return (
    matchingNodeType?.data?.name ||
    matchingNodeType?.data?.model?.name ||
    node.data.name ||
    node.data.model?.name ||
    'Unknown model'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function collectCustomFontReferencesFromValue(
  value: unknown,
  node: Node,
  fontReferencesByKey: Map<string, WorkflowCommunityPublishCustomFontReference>,
) {
  if (Array.isArray(value)) {
    value.forEach((item) => collectCustomFontReferencesFromValue(item, node, fontReferencesByKey));
    return;
  }

  if (!isRecord(value)) {
    return;
  }

  const fontOptions = value.fontOptions;
  if (isRecord(fontOptions)) {
    const font = fontOptions.font;
    if (isRecord(font) && font.type === 'custom' && isRecord(font.data) && typeof font.data.resourceKey === 'string') {
      fontReferencesByKey.set(`${font.data.resourceKey}:${node.id}`, {
        fontFamilyKey: font.data.resourceKey,
        nodeId: node.id,
        nodeName: node.data.name,
      });
    }
  }

  Object.values(value).forEach((childValue) =>
    collectCustomFontReferencesFromValue(childValue, node, fontReferencesByKey),
  );
}

function sortCustomFontReferences(
  references: WorkflowCommunityPublishCustomFontReference[],
): WorkflowCommunityPublishCustomFontReference[] {
  return references.sort(
    (a, b) => a.fontFamilyKey.localeCompare(b.fontFamilyKey) || (a.nodeName ?? '').localeCompare(b.nodeName ?? ''),
  );
}

function dedupeCustomFontReferences(
  references: WorkflowCommunityPublishCustomFontReference[],
): WorkflowCommunityPublishCustomFontReference[] {
  const referencesByKey = new Map<string, WorkflowCommunityPublishCustomFontReference>();
  references.forEach((reference) => {
    referencesByKey.set(`${reference.fontFamilyKey}:${reference.nodeId ?? ''}`, reference);
  });
  return sortCustomFontReferences(Array.from(referencesByKey.values()));
}

export function collectCustomFontReferencesFromNodes(nodes: Node[]): WorkflowCommunityPublishCustomFontReference[] {
  const fontReferencesByKey = new Map<string, WorkflowCommunityPublishCustomFontReference>();
  nodes.forEach((node) => collectCustomFontReferencesFromValue(node.data, node, fontReferencesByKey));
  return sortCustomFontReferences(Array.from(fontReferencesByKey.values()));
}

/**
 * The `isPublicOverride` for a Tool publish: a Tool is published under its OWN access, so the
 * `not_public` gate must read the tool, not the parent workflow. Mirrors the server's `isToolViewer` —
 * a tool's own visibility is always authoritative, so a Public tool under a Private workflow IS public
 * (and must not be prompted to "update access"), while a Private tool under a Public workflow is NOT.
 *
 * Returns `undefined` only for a tool view that hasn't loaded (or failed to load), which is exactly
 * the "fall back to the workflow's visibility" case the caller wants.
 */
export function getToolCommunityPublishIsPublic(
  toolAccess: { visibility: ToolVisibilityType } | undefined,
): boolean | undefined {
  if (!toolAccess) {
    return undefined;
  }
  return toolAccess.visibility === ToolVisibilityType.Public;
}

/**
 * Whether the tool is shared widely enough to publish to a Figma org: anyone in its Weave
 * workspace (Team) or anyone with the link (Public). Community publishing needs Public.
 */
export function getToolOrgPublishIsShared(
  toolAccess: { visibility: ToolVisibilityType } | undefined,
): boolean | undefined {
  if (!toolAccess) {
    return undefined;
  }
  return toolAccess.visibility === ToolVisibilityType.Team || toolAccess.visibility === ToolVisibilityType.Public;
}

export function getWorkflowCommunityPublishReadiness(args: {
  customFontFamilyKeys?: string[];
  customFontReferences?: WorkflowCommunityPublishCustomFontReference[];
  /**
   * Replaces the workflow-derived `isPublic` when the thing being published isn't the workflow itself
   * (a Tool publish — see {@link getToolCommunityPublishIsPublic}). `undefined` keeps the workflow's
   * own visibility as the source of truth.
   */
  isPublicOverride?: boolean;
  nodeTypes: NodeDefinition[];
  nodes: Node[];
  visibility: RecipeVisibilityType;
}): WorkflowCommunityPublishReadiness {
  const { visibility, nodes, nodeTypes, isPublicOverride } = args;
  const customFontReferences = dedupeCustomFontReferences([
    ...(args.customFontReferences ?? []),
    ...(args.customFontFamilyKeys ?? []).map((fontFamilyKey) => ({ fontFamilyKey })),
    ...collectCustomFontReferencesFromNodes(nodes),
  ]);
  const customFontFamilyKeys = Array.from(
    new Set(customFontReferences.map((fontReference) => fontReference.fontFamilyKey)),
  ).sort((a, b) => a.localeCompare(b));
  const isPublic = isPublicOverride ?? visibility === RecipeVisibilityType.Public;
  const figmaNodeIds = nodes
    .filter((node) => node.type === NodeType.FigmaNode)
    .map((node) => node.id)
    .sort((a, b) => a.localeCompare(b));
  const unverifiedModelsByKey = new Map<string, WorkflowCommunityPublishUnverifiedModel>();

  for (const node of nodes) {
    if (!isModelNode(node)) {
      continue;
    }

    const modelName = node.data.model?.name;
    if (!modelName) {
      continue;
    }

    const modelKey = modelName;
    const matchingNodeTypes = nodeTypes.filter((nodeType) => nodeType.data?.model?.name === modelName);
    const isVerified = matchingNodeTypes.some((nodeType) => nodeType.metadata?.isVerified === true);

    if (isVerified) {
      continue;
    }

    unverifiedModelsByKey.set(modelKey, {
      key: modelKey,
      displayName: getModelDisplayName(node, matchingNodeTypes),
    });
  }

  const unverifiedModels = Array.from(unverifiedModelsByKey.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );

  const blocker =
    customFontFamilyKeys.length > 0
      ? 'custom_fonts'
      : figmaNodeIds.length > 0
        ? 'figma_node'
        : unverifiedModels.length > 0
          ? 'unverified_models'
          : !isPublic
            ? 'not_public'
            : null;

  return {
    blocker,
    customFontFamilyKeys,
    customFontReferences,
    figmaNodeIds,
    isPublic,
    isReady: blocker === null,
    unverifiedModels,
  };
}
